import { useEffect, useMemo } from "react";

import { useAuth } from "src/lib/auth";
import {
  reportingIntegrationTypes,
  storeIntegrationTypes,
} from "src/lib/constants";
import { DEMO_STORE_MEMBERSHIP_CHANGED_EVENT } from "src/lib/demoStore";
import { useApiMutation, useApiQuery } from "src/lib/hooks/useApi";
import { useIntegration } from "src/lib/hooks/useIntegration";
import {
  buildOnboardingState,
  getOnboardingStateId,
  GLOBAL_ONBOARDING_SCOPE_KEY,
} from "src/lib/onboarding/buildOnboardingState";
import { PAYMENT_METHOD_QUERY } from "src/lib/queries/billing";
import { DEMO_STORE_STATUS_QUERY } from "src/lib/queries/integrations";
import {
  UPSERT_USER_ONBOARDING_STATE_MUTATION,
  USER_ONBOARDING_STATES_QUERY,
} from "src/lib/queries/onboarding";

/**
 * Fetches the state needed to render the onboarding checklist and exposes the
 * persistence helpers for dismiss/skip/restore interactions.
 *
 * @param {{ queryOnRender?: boolean }} [options]
 */
export const useOnboarding = ({ queryOnRender = true } = {}) => {
  const { currentUser } = useAuth();
  const {
    selectedIntegration: selectedStore,
    integrations: stores,
    loading: storeLoading,
  } = useIntegration(storeIntegrationTypes.shopify, { queryOnRender });
  const { selectedIntegration: slackIntegration, loading: slackLoading } =
    useIntegration(reportingIntegrationTypes.slack, { queryOnRender });
  const {
    queryApi: queryPaymentMethod,
    data: paymentMethodData,
    loading: paymentMethodLoading,
  } = useApiQuery({
    query: PAYMENT_METHOD_QUERY,
  });
  const {
    queryApi: queryDemoStoreStatus,
    data: demoStoreData,
    loading: demoStoreLoading,
  } = useApiQuery({
    query: DEMO_STORE_STATUS_QUERY,
  });

  const scopeKeys = useMemo(
    () =>
      [GLOBAL_ONBOARDING_SCOPE_KEY, selectedStore?.id]
        .filter(Boolean)
        .filter((value, index, list) => list.indexOf(value) === index),
    [selectedStore?.id]
  );

  const {
    queryApi: queryUserOnboardingStates,
    data: onboardingData,
    loading: onboardingLoading,
  } = useApiQuery({
    query: USER_ONBOARDING_STATES_QUERY,
  });
  const { mutationApi: upsertUserOnboardingState, loading: upsertLoading } =
    useApiMutation({
      mutation: UPSERT_USER_ONBOARDING_STATE_MUTATION,
    });

  useEffect(() => {
    if (currentUser?.id) {
      queryUserOnboardingStates({
        scopeKeys,
      });
    }
  }, [currentUser?.id, queryUserOnboardingStates, scopeKeys]);

  useEffect(() => {
    if (currentUser?.id && queryOnRender) {
      queryPaymentMethod();
      queryDemoStoreStatus();
    }
  }, [
    currentUser?.id,
    queryDemoStoreStatus,
    queryOnRender,
    queryPaymentMethod,
  ]);

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }

    const handleDemoStoreMembershipChanged = () => {
      queryDemoStoreStatus();
    };

    window.addEventListener(
      DEMO_STORE_MEMBERSHIP_CHANGED_EVENT,
      handleDemoStoreMembershipChanged
    );

    return () => {
      window.removeEventListener(
        DEMO_STORE_MEMBERSHIP_CHANGED_EVENT,
        handleDemoStoreMembershipChanged
      );
    };
  }, [currentUser?.id, queryDemoStoreStatus]);

  const isStoreAdmin = useMemo(() => {
    if (!selectedStore || !currentUser?.id) {
      return !selectedStore;
    }

    return selectedStore.users?.some(
      ({ user, role }) => user.id === currentUser.id && role === "ADMIN"
    );
  }, [currentUser?.id, selectedStore]);

  const onboardingStates = useMemo(
    () => onboardingData?.userOnboardingStates || [],
    [onboardingData?.userOnboardingStates]
  );
  const onboardingStatesById = useMemo(
    () =>
      Object.fromEntries(
        onboardingStates.map((state) => [
          getOnboardingStateId(state.scopeKey, state.key),
          state,
        ])
      ),
    [onboardingStates]
  );

  const hasPaymentMethod = Boolean(paymentMethodData?.paymentMethod?.last4);
  const trialDaysLeft =
    currentUser?.trialDaysLeft ?? currentUser?.trialDaysLeft ?? -1;

  const dismissPrompt = async ({ scopeKey, key, payload }) => {
    await upsertUserOnboardingState({
      input: {
        scopeKey,
        key,
        payload,
        dismissedAt: new Date().toISOString(),
      },
    });

    await queryUserOnboardingStates({
      scopeKeys,
    });
    await queryDemoStoreStatus();
  };

  const skipTask = async ({ scopeKey, key, payload }) => {
    await upsertUserOnboardingState({
      input: {
        scopeKey,
        key,
        completedAt: new Date().toISOString(),
        payload: {
          ...(payload || {}),
          skipped: true,
        },
      },
    });

    await queryUserOnboardingStates({
      scopeKeys,
    });
    await queryDemoStoreStatus();
  };

  const restoreTask = async ({ scopeKey, key, payload }) => {
    await upsertUserOnboardingState({
      input: {
        scopeKey,
        key,
        completedAt: null,
        dismissedAt: null,
        payload: {
          ...(payload || {}),
          skipped: false,
        },
      },
    });

    await queryUserOnboardingStates({
      scopeKeys,
    });
    await queryDemoStoreStatus();
  };

  const onboarding = useMemo(
    () =>
      buildOnboardingState({
        selectedStore,
        stores,
        slackIntegration,
        onboardingStatesById,
        currentUserId: currentUser?.id || null,
        hasPaymentMethod,
        billingLoading: paymentMethodLoading,
        trialDaysLeft,
        isStoreAdmin,
        demoStoreStatus: demoStoreData?.demoStoreStatus || null,
      }),
    [
      currentUser?.id,
      hasPaymentMethod,
      isStoreAdmin,
      onboardingStatesById,
      paymentMethodLoading,
      selectedStore,
      slackIntegration,
      stores,
      trialDaysLeft,
      demoStoreData?.demoStoreStatus,
    ]
  );

  return {
    selectedStore,
    slackIntegration,
    isStoreAdmin,
    onboarding,
    onboardingStates,
    onboardingStatesById,
    dismissPrompt,
    skipTask,
    restoreTask,
    loading:
      storeLoading ||
      slackLoading ||
      onboardingLoading ||
      upsertLoading ||
      paymentMethodLoading ||
      demoStoreLoading,
  };
};
